import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, TextField, Typography } from "@mui/material";

import { ACCENT } from "./MeteredParts";

// The "full data" window behind a card's expand button — same pattern as the Copilot dashboard's top-engineers
// list: green rule under the title, optional search and extra controls on the right, a loading overlay while
// it fetches, and Previous / Next paging in the footer. The body (a table) is passed in as children.
const FullListDialog = ({
  open,
  onClose,
  title,
  subtitle,
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  controls,
  loading = false,
  page,
  totalPages,
  totalCount,
  perPage,
  noun = "rows",
  onPageChange,
  children,
}) => {
  const first = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, totalCount);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `3px solid ${ACCENT}`, py: 2, px: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>{title}</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {onSearchChange && (
            <TextField
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              size="small"
              inputProps={{ "aria-label": searchPlaceholder }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: "1.2rem", color: "#999" }} /></InputAdornment> }}
              sx={{ width: 240, "& .MuiOutlinedInput-root": { borderRadius: "6px", backgroundColor: "#f9f9f9" } }}
            />
          )}
          {controls}
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: "#555" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: 560, position: "relative" }}>
        {loading && (
          <Box
            role="status"
            aria-label="Loading"
            sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(255, 255, 255, 0.85)", zIndex: 10 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <CircularProgress sx={{ color: ACCENT }} size={40} />
              <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>Loading...</Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1, overflowY: "auto", opacity: loading ? 0.5 : 1 }}>{children}</Box>
        <Divider />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 1.5, backgroundColor: "#fafafa", opacity: loading ? 0.5 : 1 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#777" }}>
            Showing {first}–{last} of {totalCount.toLocaleString("en-US")} {noun}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" aria-label="Previous page" disabled={page === 1 || loading} onClick={() => onPageChange(page - 1)} sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: "0.82rem", color: "#555", minWidth: 70, textAlign: "center" }}>
              Page {page} of {totalPages}
            </Typography>
            <IconButton size="small" aria-label="Next page" disabled={page >= totalPages || loading} onClick={() => onPageChange(page + 1)} sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FullListDialog;
